import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import { ROUTES } from "utils/router";

/**
 * Keeps account scoped pages (e.g. `/home/:idaccount`) from rendering for a
 * user that has no usable account.
 *
 * Without this, a user whose account list comes back empty - or who lands on an
 * `:idaccount` that is not part of their list (stale bookmark, `/home/undefined`,
 * revoked access) - stays on the dashboard with empty widgets, an empty account
 * dropdown and "Please select account." errors from the sidebar.
 *
 * The redirect target is the transition page, which either resolves the user's
 * favourite/last account or shows the "no account available" message.
 *
 * The check only runs once the account list request has settled, so the page is
 * still mounted while loading. That matters: the request itself is fired by the
 * account dropdown inside the layout of the page being guarded.
 */
const useAccountGuard = () => {
  const { idaccount } = useParams();
  const navigate = useNavigate();
  const { accounts = [], accountsStatus } = useSelector(
    (state) => state?.account || {}
  );

  useEffect(() => {
    if (accountsStatus !== "succeeded") return;

    // No account at all -> nothing to show on an account scoped page.
    if (!accounts.length) {
      navigate(ROUTES.TRANSITION, { replace: true });
      return;
    }

    // Account in the url does not belong to this user (or is missing).
    const isKnownAccount = accounts.some(
      (account) =>
        String(account?.account_id) === String(idaccount)
    );

    if (!isKnownAccount) {
      navigate(ROUTES.TRANSITION, { replace: true });
    }
  }, [accountsStatus, accounts, idaccount, navigate]);
};

export default useAccountGuard;
